// Batch & process reports: templates are built per client in the admin panel, clients run them.

import { API_BASE } from './api-base';

export type BatchSourceType = 'controller' | 'vfd' | 'io' | 'plc' | 'energy_meter' | 'scanner';
export type BatchStartMode = 'manual' | 'trigger' | 'both';

export interface BatchTagRef {
  sourceType: BatchSourceType;
  deviceId: string;
  deviceName: string;
  tagKey: string;
  tagName: string;
  unit?: string;
  decimals?: number;
}

export interface BatchTrigger extends BatchTagRef {
  threshold: number;
  holdSec: number;
}

export interface BatchSections {
  summary: boolean;
  charts: boolean;
  dataTable: boolean;
  ioEvents: boolean;
  signatureLines: boolean;
}

export interface BatchField {
  label: string;
  required: boolean;
}

export interface BatchTemplate {
  id: string;
  name: string;
  kind: 'batch' | 'process';
  isActive: boolean;
  description?: string;
  timeZone: string;
  sampleSec: number;
  tags: BatchTagRef[];
  sections: BatchSections;
  startMode?: BatchStartMode;
  numberPrefix?: string;
  product?: string;
  fields?: BatchField[];
  trigger?: BatchTrigger;
}

export interface BatchCatalogDevice {
  sourceType: BatchSourceType;
  deviceId: string;
  deviceName: string;
  tags: { tagKey: string; tagName: string; unit?: string; decimals?: number }[];
}

export interface BatchSignature {
  id: string;
  signedByName?: string | null;
  signedByEmail?: string | null;
  signedByRole?: string | null;
  reason: string;
  meaning: string;
  signedAt: string;
}

export interface Batch {
  id: string;
  templateId: string;
  templateName: string;
  batchNumber: string;
  product?: string | null;
  fields: Record<string, string>;
  templateFields: BatchField[];
  status: 'running' | 'completed' | 'aborted';
  startedAt: string;
  endedAt?: string | null;
  startedByName?: string | null;
  startSource: 'manual' | 'trigger';
  endedByName?: string | null;
  endSource?: 'manual' | 'trigger' | null;
  notes?: string | null;
  signatures: BatchSignature[];
}

export const SOURCE_LABELS: Record<BatchSourceType, string> = {
  controller: 'Controller',
  vfd: 'VFD',
  io: 'IO module',
  plc: 'PLC',
  energy_meter: 'Energy meter',
  scanner: 'Scanner',
};

export const SECTION_LABELS: Record<keyof BatchSections, string> = {
  summary: 'Summary table (start, end, min, max, average, change)',
  charts: 'Trend charts',
  dataTable: 'Table of recorded values',
  ioEvents: 'Digital input ON time and events',
  signatureLines: 'Blank lines for hand signatures',
};

export const SIGNATURE_MEANINGS = ['Review', 'Approval', 'Verification', 'Release'];

const headers = (token: string) => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' });

async function request<T>(url: string, token: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${url}`, { ...init, headers: { ...headers(token), ...(init.headers || {}) } });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || `Request failed (HTTP ${res.status})`);
  return body as T;
}

async function download(url: string, token: string, fileName: string) {
  const res = await fetch(`${API_BASE}${url}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.error || `Download failed (HTTP ${res.status})`);
  }
  const objectUrl = window.URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(objectUrl);
}

const safeFileName = (name: string) => name.replace(/[^a-z0-9_-]+/gi, '_');

export const batchApi = {
  // ---------------- admin
  setup: (token: string, clientId: string) =>
    request<{ enabled: boolean; templates: BatchTemplate[]; catalog: BatchCatalogDevice[] }>(`/api/admin/clients/${clientId}/batch-setup`, token),
  setEnabled: (token: string, clientId: string, enabled: boolean) =>
    request<{ enabled: boolean }>(`/api/admin/clients/${clientId}/batch-reports-enabled`, token, { method: 'PUT', body: JSON.stringify({ enabled }) }),
  createTemplate: (token: string, clientId: string, template: Partial<BatchTemplate>) =>
    request<{ template: BatchTemplate }>(`/api/admin/clients/${clientId}/batch-templates`, token, { method: 'POST', body: JSON.stringify(template) }).then((r) => r.template),
  updateTemplate: (token: string, clientId: string, id: string, template: Partial<BatchTemplate>) =>
    request<{ template: BatchTemplate }>(`/api/admin/clients/${clientId}/batch-templates/${id}`, token, { method: 'PUT', body: JSON.stringify(template) }).then((r) => r.template),
  deleteTemplate: (token: string, clientId: string, id: string) =>
    request<{ success: boolean }>(`/api/admin/clients/${clientId}/batch-templates/${id}`, token, { method: 'DELETE' }),

  // ---------------- client
  overview: (token: string, clientId: string, q = '') =>
    request<{ templates: BatchTemplate[]; running: Batch[]; batches: Batch[] }>(`/api/clients/${clientId}/batch-reports${q ? `?q=${encodeURIComponent(q)}` : ''}`, token),
  start: (token: string, clientId: string, body: { templateId: string; batchNumber?: string; product?: string; fields?: Record<string, string> }) =>
    request<{ batch: Batch }>(`/api/clients/${clientId}/batches`, token, { method: 'POST', body: JSON.stringify(body) }).then((r) => r.batch),
  stop: (token: string, clientId: string, id: string, body: { status: 'completed' | 'aborted'; notes?: string }) =>
    request<{ batch: Batch }>(`/api/clients/${clientId}/batches/${id}/stop`, token, { method: 'POST', body: JSON.stringify(body) }).then((r) => r.batch),
  updateDetails: (token: string, clientId: string, id: string, body: { product?: string; fields?: Record<string, string> }) =>
    request<{ batch: Batch }>(`/api/clients/${clientId}/batches/${id}`, token, { method: 'PUT', body: JSON.stringify(body) }).then((r) => r.batch),
  sign: (token: string, clientId: string, id: string, body: { password: string; reason: string; meaning: string }) =>
    request<{ signatureId: string }>(`/api/clients/${clientId}/batches/${id}/sign`, token, { method: 'POST', body: JSON.stringify(body) }),
  downloadBatch: (token: string, clientId: string, batch: Batch, format: 'pdf' | 'xlsx') =>
    download(`/api/clients/${clientId}/batches/${batch.id}/${format}`, token, `${safeFileName(batch.batchNumber)}.${format}`),
  downloadSigned: (token: string, clientId: string, batch: Batch, signature: BatchSignature) =>
    download(`/api/clients/${clientId}/batches/${batch.id}/signatures/${signature.id}/pdf`, token, `${safeFileName(batch.batchNumber)}_signed_${signature.meaning.toLowerCase()}.pdf`),
  downloadProcess: (token: string, clientId: string, template: BatchTemplate, format: 'pdf' | 'xlsx', from: Date, to: Date) =>
    download(
      `/api/clients/${clientId}/batch-templates/${template.id}/process-report/${format}?from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}`,
      token,
      `${safeFileName(template.name)}_${from.toISOString().slice(0, 10)}.${format}`,
    ),
};
